import { env } from './env';

type SentryModule = typeof import('@sentry/react');

let sentry: SentryModule | null = null;
let starting: Promise<void> | null = null;

/** Query parameters that must never leave the browser in an error report. */
const SECRET_PARAMS = /([?&](token|k|code)=)[^&]*/gi;

function scrub(value: string): string {
  return value.replace(SECRET_PARAMS, '$1redacted');
}

/**
 * Loads Sentry only when a DSN is configured, so the SDK stays out of the
 * bundle for previews and local development.
 */
export function startMonitoring(): Promise<void> {
  const dsn = env?.VITE_SENTRY_DSN;
  if (!dsn) return Promise.resolve();

  starting ??= import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      // Errors only: the shop has no interactive latency budget worth tracing.
      tracesSampleRate: 0,

      beforeSend(event) {
        // The order-status token and the Daraja callback secret both ride in
        // query strings; they are bearer credentials and must not be stored.
        if (event.request?.url) event.request.url = scrub(event.request.url);
        delete event.user;
        return event;
      },

      beforeBreadcrumb(breadcrumb) {
        const url = breadcrumb.data?.url;
        if (typeof url === 'string' && SECRET_PARAMS.test(url)) return null;
        SECRET_PARAMS.lastIndex = 0;
        return breadcrumb;
      },
    });

    sentry = Sentry;
  });

  return starting;
}

/** Reports an error if monitoring is active; always logs locally. */
export function captureException(error: unknown, context?: Record<string, unknown>) {
  if (sentry) {
    sentry.captureException(error, context ? { extra: context } : undefined);
    return;
  }

  console.error(error, context ?? '');
}
